<template>
  <div class="tips-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <h1>{{ t('tips.title') }}</h1>
        <p class="page-subtitle">{{ t('tips.subtitle') }}</p>
      </div>
    </section>

    <!-- Tips Content -->
    <section class="tips-content">
      <div class="container">
        <!-- Poker Tips -->
        <div class="game-tips">
          <h2>{{ t('games.poker.title') }} Tips</h2>
          <div class="tips-grid">
            <div class="tip-card">
              <div class="tip-number">1</div>
              <h3>{{ t('tips.poker.tip1.title') }}</h3>
              <p>{{ t('tips.poker.tip1.desc') }}</p>
            </div>
            <div class="tip-card">
              <div class="tip-number">2</div>
              <h3>{{ t('tips.poker.tip2.title') }}</h3>
              <p>{{ t('tips.poker.tip2.desc') }}</p>
            </div>
            <div class="tip-card">
              <div class="tip-number">3</div>
              <h3>{{ t('tips.poker.tip3.title') }}</h3>
              <p>{{ t('tips.poker.tip3.desc') }}</p>
            </div>
            <div class="tip-card">
              <div class="tip-number">4</div>
              <h3>{{ t('tips.poker.tip4.title') }}</h3>
              <p>{{ t('tips.poker.tip4.desc') }}</p>
            </div>
          </div>
        </div>

        <!-- Sudoku Tips -->
        <div class="game-tips">
          <h2>{{ t('games.sudoku.title') }} Tips</h2>
          <div class="tips-grid">
            <div class="tip-card">
              <div class="tip-number">1</div>
              <h3>{{ t('tips.sudoku.tip1.title') }}</h3>
              <p>{{ t('tips.sudoku.tip1.desc') }}</p>
            </div>
            <div class="tip-card">
              <div class="tip-number">2</div>
              <h3>{{ t('tips.sudoku.tip2.title') }}</h3>
              <p>{{ t('tips.sudoku.tip2.desc') }}</p>
            </div>
            <div class="tip-card">
              <div class="tip-number">3</div>
              <h3>{{ t('tips.sudoku.tip3.title') }}</h3>
              <p>{{ t('tips.sudoku.tip3.desc') }}</p>
            </div>
            <div class="tip-card">
              <div class="tip-number">4</div>
              <h3>{{ t('tips.sudoku.tip4.title') }}</h3>
              <p>{{ t('tips.sudoku.tip4.desc') }}</p>
            </div>
          </div>
        </div>

        <!-- Riddles Tips -->
        <div class="game-tips">
          <h2>{{ t('games.riddles.title') }} Tips</h2>
          <div class="tips-grid">
            <div class="tip-card">
              <div class="tip-number">1</div>
              <h3>{{ t('tips.riddles.tip1.title') }}</h3>
              <p>{{ t('tips.riddles.tip1.desc') }}</p>
            </div>
            <div class="tip-card">
              <div class="tip-number">2</div>
              <h3>{{ t('tips.riddles.tip2.title') }}</h3>
              <p>{{ t('tips.riddles.tip2.desc') }}</p>
            </div>
            <div class="tip-card">
              <div class="tip-number">3</div>
              <h3>{{ t('tips.riddles.tip3.title') }}</h3>
              <p>{{ t('tips.riddles.tip3.desc') }}</p>
            </div>
            <div class="tip-card">
              <div class="tip-number">4</div>
              <h3>{{ t('tips.riddles.tip4.title') }}</h3>
              <p>{{ t('tips.riddles.tip4.desc') }}</p>
            </div>
          </div>
        </div>

        <!-- Back Button -->
        <button class="btn-back" @click="goHome">{{ t('buttons.backHome') }}</button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { useI18n } from '@/utils/i18n'

const { t } = useI18n()

const goHome = () => {
  window.location.hash = '#/'
}
</script>

<style scoped>
.tips-page {
  min-height: 100vh;
  background: var(--color-bg-light);
}

/* Header */
.page-header {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-5xl) var(--space-base);
  text-align: center;
}

.page-header h1 {
  font-size: clamp(28px, 6vw, 48px);
  margin: 0 0 var(--space-base) 0;
  font-weight: var(--font-weight-bold);
}

.page-subtitle {
  font-size: var(--font-size-body);
  margin: 0;
  opacity: 0.95;
}

/* Content */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-4xl) var(--space-base);
}

.tips-content {
  background: var(--color-bg-light);
}

/* Game Tips Section */
.game-tips {
  margin-bottom: var(--space-5xl);
}

.game-tips h2 {
  font-size: var(--font-size-h3);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-2xl) 0;
  font-weight: var(--font-weight-bold);
  text-align: center;
}

/* Tips Grid */
.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-2xl);
  margin-bottom: var(--space-3xl);
}

/* Tip Card */
.tip-card {
  background: var(--color-bg-card);
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  transition: all var(--transition-base) var(--ease-in-out));
  position: relative;
}

.tip-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-4px);
  border-color: var(--color-primary-orange);
}

.tip-number {
  position: absolute;
  top: -16px;
  left: 20px;
  width: 40px;
  height: 40px;
  background: var(--gradient-primary);
  color: white;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-h5);
}

.tip-card h3 {
  font-size: var(--font-size-h5);
  color: var(--color-text-primary);
  margin: var(--space-lg) 0 var(--space-md) 0;
  font-weight: var(--font-weight-bold);
  padding-top: var(--space-base);
}

.tip-card p {
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.7;
}

/* Back Button */
.btn-back {
  display: block;
  margin: var(--space-3xl) auto 0;
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-light);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.btn-back:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
  background: rgba(255, 107, 53, 0.05);
}

/* Responsive */
@media (max-width: 640px) {
  .page-header {
    padding: var(--space-3xl) var(--space-base);
  }

  .container {
    padding: var(--space-2xl) var(--space-base);
  }

  .game-tips {
    margin-bottom: var(--space-3xl);
  }

  .game-tips h2 {
    font-size: var(--font-size-h4);
    margin-bottom: var(--space-lg);
  }

  .tips-grid {
    grid-template-columns: 1fr;
    gap: var(--space-lg);
  }

  .tip-card {
    padding: var(--space-lg);
    padding-top: var(--space-2xl);
  }

  .tip-number {
    width: 32px;
    height: 32px;
    font-size: var(--font-size-body);
  }

  .tip-card h3 {
    margin-top: var(--space-md);
    font-size: var(--font-size-body);
  }

  .btn-back {
    width: 100%;
  }
}
</style>
